import React from "react";
import * as XLSX from "xlsx";

import Button from "../buttons/Button";
import ButtonThemes from "../../types/enums/ButtonThemes";
import WebRequest from "../../communication/WebRequest";
import Utilities from "../../foundation/Utility";
import { DataSet } from "../../types/data/DataSet";
import { DataTable, DataTableRow } from "../../types/data/DataTable";
import {
    SpreadsheetColumnDefinition,
    SpreadsheetConfiguration,
    SpreadsheetTableDefinition,
} from "../../types/data/SpreadsheetConfiguration";
import Checkbox from "../inputform/Checkbox";
import DropDown, { DropDownOption } from "../inputform/DropDown";
import GeneralContainer from "../containers/GeneralContainer";
import { FlexBehaviour, FlexDirection, Side } from "../../types/system/Layout";

import "./Import.scss";
import ImportGrid from "./ImportGrid";
import Panel from "../panels/Panel";

export interface DataMapping {
    Source: string;
    Target?: SpreadsheetTableDefinition;
}

interface ImportProps {
    className?: string;

    noConfig?: boolean;
    config: SpreadsheetConfiguration;
    template: string;
    templateName?: string;

    onReady?: (mappings: Array<DataMapping>, spreadsheets: DataSet) => void;
}

interface ImportState {
    firstRowHeader: boolean;

    importData?: DataSet;
    config?: SpreadsheetConfiguration;

    /** Array of source worksheet options available for selection */
    sourceTableItems: DropDownOption[];

    /** Array of target data type options available for mapping */
    targetTableItems: DropDownOption[];

    /** Currently selected source worksheet */
    selectedSource: string;

    /** Currently selected target data type */
    selectedTarget: string;

    /** Array of worksheet-to-datatype mappings */
    importMappings: Array<DataMapping>;

    /** Column definitions for the currently selected source, used for display and mapping */
    selectedMapping?: DataMapping;

    /** Whether the first row of data contains column headers */
    hasColumns: boolean;

    /** Data rows for the currently displayed worksheet */
    displayData: DataTable;
}

class Import extends React.Component<ImportProps, ImportState> {
    state = {
        firstRowHeader: true,
    } as ImportState;

    async downloadTemplate() {
        await (
            await WebRequest.raw(this.props.template)
        ).download(this.props.templateName || "Import_Template.xlsx");
    }

    async uploadTemplate() {
        const file = (await Utilities.uploadFile(".xlsx,.csv", 1))[0];

        let dataset: DataSet | undefined = undefined;
        if (file.name.endsWith(".xlsx")) {
            const wb: XLSX.WorkBook = XLSX.read(file.data, { type: "base64" });
            dataset = await this.parseXLSXFile(wb);
        } else if (file.name.endsWith(".csv")) {
            dataset = await this.parseCSVFile(file.data.fromBase64String());
        }

        const config = structuredClone(this.props.config);

        this.setState({
            config: config,
            importData: dataset,
        });

        // Build UI data structures
        const selectedSource = this.buildSourceTables(dataset);
        const importMappings = this.buildMappings(dataset, config);

        this.processSheet(
            selectedSource?.value,
            dataset,
            importMappings,
            config,
        );

        this.setState({
            importMappings: importMappings,
            selectedMapping: importMappings.find(
                item => item.Source === selectedSource?.value,
            ),
        });

        this.compileOutput(importMappings, dataset);
    }

    async parseXLSXFile(wb: XLSX.WorkBook) {
        const result: DataSet = {};

        for (const sheetName of wb.SheetNames) {
            const ws = wb.Sheets[sheetName];
            result[sheetName] = [];

            const temp = XLSX.utils.sheet_to_json(ws, {
                header: 1,
            });

            const fields = new Array<string>();
            for (const [index, row] of (temp as string[][]).entries()) {
                if (row.length === 0 || row.every(cell => cell === ""))
                    continue;

                if (this.state.firstRowHeader && index === 0)
                    for (const field of row) fields.push(field);
                else {
                    const dataRow = {} as DataTableRow;
                    for (const [fieldIndex, field] of row.entries())
                        dataRow[fields[fieldIndex] ?? `Field_${fieldIndex}`] =
                            field;
                    result[sheetName].push(dataRow);
                }
            }
        }

        return result;
    }

    async parseCSVFile(data: string) {
        data = data.replace(/\r/g, "");

        const result: DataSet = {};
        const sheetName = this.props.config.Tables[0].Name;

        result[sheetName] = [];

        const fields = new Array<string>();
        for (const [index, row] of data
            .split("\n")
            .map(line => line.split(","))
            .entries()) {
            if (row.length === 0 || row.every(cell => cell === "")) continue;

            if (this.state.firstRowHeader && index === 0)
                for (const field of row) fields.push(field);
            else {
                const dataRow = {} as DataTableRow;
                for (const [fieldIndex, field] of row.entries())
                    dataRow[fields[fieldIndex] ?? `Field_${fieldIndex}`] =
                        field;
                result[sheetName].push(dataRow);
            }
        }

        return result;
    }

    /**
     * Build initial mappings between source worksheets and target data types
     *
     * @author Liam Pearce
     * @private
     *
     * @description
     * Creates mapping entries for each worksheet in the imported data.
     * Attempts to auto-match worksheets to target types by name.
     * For matched worksheets with data, also auto-maps columns where
     * source and target field names match exactly.
     *
     * This provides intelligent defaults that often require minimal
     * user adjustment for standardized imports.
     */
    private buildMappings(
        importData: DataSet,
        config: SpreadsheetConfiguration,
    ) {
        const tempMappings = structuredClone(this.state.importMappings ?? []);

        // Create mapping entry for each worksheet
        for (const table in importData)
            tempMappings.push({
                Source: table,
                Target: config.Tables.find(item => item.Name === table),
            });

        // Auto-map columns where names match exactly
        for (const mapping of tempMappings) {
            if (
                !!mapping.Target &&
                !!importData?.[mapping.Source] &&
                importData[mapping.Source].length > 0
            ) {
                const columns = [];
                const table = importData?.[mapping.Source];
                const tableColumns = Object.keys(table.first());

                for (const tableCol of tableColumns) {
                    const tempMap = mapping.Target.Columns.find(
                        col => col.Field === tableCol || col.Name === tableCol,
                    );
                    const column = tempMap
                        ? structuredClone(tempMap)
                        : { Name: tableCol, Field: tableCol };
                    column.Target = tempMap?.Field;
                    columns.push(column);
                }

                mapping.Target.Columns = columns;
            }
        }

        this.setState({ importMappings: tempMappings });

        return tempMappings;
    }

    /**
     * Build list of source worksheets
     *
     * @author Liam Pearce
     * @private
     *
     * @description
     * Creates an array of source worksheet options for the source dropdown.
     * Automatically selects the first worksheet if any exist.
     * Each option contains both a display name and value for the select element.
     */
    private buildSourceTables(importData: DataSet) {
        const source = new Array<{ name: string; value: string }>();

        // Create option for each worksheet
        for (const table in importData) {
            source.push({
                name: table,
                value: table,
            });
        }

        const selectedSource = source.length > 0 ? source[0] : null;
        // Auto-select first source if available
        this.setState({
            sourceTableItems: source.map((item, index) => ({
                text: item.name,
                value: item.value,
                order: index + 1,
            })),
            selectedSource: selectedSource?.value ?? "",
        });

        return selectedSource;
    }

    /**
     * Handle source worksheet selection change
     *
     * @author Liam Pearce
     * @private
     * @async
     *
     * @description
     * Called when user selects a different source worksheet from the dropdown.
     * Waits for Angular digest cycle to complete, then reprocesses the selected
     * worksheet to update the display.
     */
    private async sourceChanged(value: string) {
        this.setState({
            selectedSource: value,
            selectedMapping: this.state.importMappings?.find(
                item => item.Source === value,
            ),
        });

        // Reprocess the newly selected worksheet
        this.processSheet(
            value,
            this.state.importData!,
            this.state.importMappings!,
            this.state.config!,
        );
    }

    /**
     * Build list of available target data types
     *
     * @author Liam Pearce
     * @private
     * @param {string} source - Name of the current source worksheet
     *
     * @description
     * Creates an array of target data type options for the target dropdown.
     * Excludes targets that are already mapped to other sources (to prevent
     * duplicate mappings). Selects the current target for this source if one exists.
     * Always includes a "[Remove Mapping]" option.
     */
    private buildTargetTables(
        source: string,
        importMappings: DataMapping[],
        config: SpreadsheetConfiguration,
    ) {
        let index = 1;

        // Find current target for this source
        const sourceMapping = importMappings.find(
            item => item.Source === source,
        );

        // Build list of available targets (excluding those already mapped elsewhere)
        const targetEntry = config.Tables.filter(
            target =>
                !importMappings.find(
                    item =>
                        item.Target?.Name === target.Name &&
                        item.Source !== source,
                ),
        ).map(target => ({
            text: target.Display ?? target.Name,
            value: target.Name,
            order: index++,
        }));

        // Add option to remove mapping
        if (!!sourceMapping?.Target)
            targetEntry.push({
                text: "[Remove Mapping]",
                value: "[Remove Mapping]",
                order: index++,
            });

        this.setState({
            targetTableItems: targetEntry,
            selectedTarget: sourceMapping?.Target?.Name ?? "",
        });
    }

    /**
     * Handle target data type selection change
     *
     * @author Liam Pearce
     * @private
     * @async
     *
     * @description
     * Called when user selects a different target data type for mapping.
     * Updates the mapping configuration and rebuilds column headers based on
     * the new target structure. If "[Remove Mapping]" is selected, clears the
     * target mapping for the current source.
     */
    private async targetChanged(value: string) {
        // Get current source and its mapping
        const source = this.state.selectedSource;
        const sourceMapping = structuredClone(this.state.selectedMapping);
        const targetTableItems = structuredClone(this.state.targetTableItems);

        // Handle removal of mapping
        if (value === "[Remove Mapping]") {
            sourceMapping.Target = undefined;
            this.setState({
                selectedTarget: undefined,
                targetTableItems: targetTableItems.filter(
                    item => item.value !== value,
                ),
            });
        } else {
            // Set new target mapping
            sourceMapping.Target = this.state.config!.Tables.find(
                item => item.Name === value,
            );

            if (
                !targetTableItems.find(
                    item => item.value === "[Remove Mapping]",
                )
            )
                targetTableItems.push({
                    text: "[Remove Mapping]",
                    value: "[Remove Mapping]",
                    order: targetTableItems.length + 1,
                });

            const columns =
                this.props.config.Tables.find(item => item.Name === source)
                    ?.Columns ?? [];

            for (const column of sourceMapping.Target.Columns)
                if (!columns.some(item => item.Field === column.Target))
                    column.Target = undefined;

            this.setState({
                selectedTarget: value,
                targetTableItems: targetTableItems,
            });
        }

        // Rebuild column headers for the new target structure
        this.buildColumnHeaders(source, this.state.importData!, sourceMapping);

        const importMappings = structuredClone(this.state.importMappings!);
        const matchMappings = importMappings.find(
            item => item.Source === sourceMapping.Source,
        );
        importMappings.replace(matchMappings!, sourceMapping);
        this.setState({ importMappings, selectedMapping: sourceMapping });

        this.compileOutput(importMappings, this.state.importData!);
    }

    /**
     * Build column headers for a given source worksheet
     *
     * @author Liam Pearce
     * @private
     * @param {string} source - Name of the source worksheet
     *
     * @description
     * Creates column definition objects for each column in the source worksheet.
     * For each column:
     * 1. Finds default mapping from configuration (if exists)
     * 2. Creates column definition with target or "bad" marker
     * 3. Builds list of available target options (unused fields)
     * 4. Adds "[Remove]" option
     *
     * This enables the UI to display dropdowns for column mapping with
     * appropriate options and current selections.
     */
    private buildColumnHeaders(
        source: string,
        importData: DataSet,
        mappings: DataMapping,
    ) {
        if (!mappings.Target) return;

        // Get data for the selected source worksheet
        const table = importData?.[source];

        // Exit if no data
        if (!table || table.length === 0) return;

        const columns =
            this.props.config.Tables.find(
                item => item.Name === mappings.Target!.Name,
            )?.Columns ?? [];

        // Build options list for each column
        for (const column of mappings.Target.Columns) {
            // Include target fields that are either:
            // 1. The current target for this column
            // 2. Not yet mapped to any other column
            column.Options =
                columns.filter(
                    def =>
                        column.Target === def.Field ||
                        !mappings.Target.Columns.some(
                            subCol => subCol.Target === def.Field,
                        ),
                ) ?? [];

            // Add "[Remove]" option to clear mapping
            if (column.Target !== undefined)
                column.Options.push({
                    Field: "[Remove]",
                    Name: "[Remove]",
                });
            // Add "Bad" option to clear mapping
            else
                column.Options.push({
                    Bad: true,
                    Field: undefined,
                    Name: `![${column.Field}]`,
                });
        }
    }

    /**
     * Process the currently selected worksheet
     *
     * @author Liam Pearce
     * @private
     *
     * @description
     * Central method for updating the display when the source selection changes.
     * Performs a complete rebuild of the UI state:
     * 1. Clears current display data
     * 2. Builds target options for the selected source
     * 3. Builds column headers and mappings
     * 4. Sets display data to the selected worksheet
     * 5. Triggers Angular digest if needed
     */
    private processSheet(
        source: string,
        importData: DataSet,
        importMappings: DataMapping[],
        config: SpreadsheetConfiguration,
    ) {
        const table = importData?.[source];
        const mapping = importMappings.find(item => item.Source === source);

        // Rebuild UI for selected source
        this.buildTargetTables(source, importMappings, config);
        this.buildColumnHeaders(source, importData, mapping);

        // Set data for display
        this.setState({ displayData: table });
    }

    /**
     * Handle column mapping change
     *
     * @author Liam Pearce
     * @private
     * @async
     * @param {SpreadsheetColumnDefinition} column - The column that was changed
     *
     * @description
     * Called when user changes the mapping for a specific column. Handles:
     * - Clearing previous mappings to prevent duplicates
     * - Removing mappings (when "[Remove]" is selected)
     * - Creating/updating column-to-field mappings
     * - Managing "bad" column markers for unmapped fields
     *
     * The method ensures that each source column maps to at most one target field
     * and maintains visual indicators for unmapped columns.
     */
    private async columnChanged(
        column: SpreadsheetColumnDefinition,
        value: string,
    ) {
        const sourceMapping = structuredClone(this.state.selectedMapping!);
        const working = sourceMapping!.Target.Columns.find(
            item => item.Field === column.Field,
        );

        // Handle removal of column mapping
        if (value === "[Remove]") {
            // Find or create "bad" marker for unmapped column
            working.Target = undefined;
        } else {
            // Set new mapping
            working.Target = value;
        }

        const columns = sourceMapping!.Target.Columns;
        const match = columns.find(col => col.Field === working.Field);
        columns.replace(match!, working);

        this.buildColumnHeaders(
            this.state.selectedSource,
            this.state.importData!,
            sourceMapping,
        );

        const importMappings = structuredClone(this.state.importMappings!);
        const matchMappings = importMappings.find(
            item => item.Source === sourceMapping.Source,
        );
        importMappings.replace(matchMappings!, sourceMapping);
        this.setState({ importMappings, selectedMapping: sourceMapping });

        this.compileOutput(importMappings, this.state.importData!);
    }

    /**
     * Compile mapped data into final output format
     *
     * @author Liam Pearce
     * @private
     *
     * @description
     * Creates the final output by transforming source data according to the
     * configured mappings. For each mapped worksheet:
     * 1. Creates empty target dataset
     * 2. Iterates through source records
     * 3. Maps fields from source to target based on column mappings
     * 4. Builds DataPackage with target name and transformed data
     *
     * The compiled output is sent to the parent component via the onReady callback,
     * enabling immediate use of the imported data.
     */
    private compileOutput(importMappings: DataMapping[], importData: DataSet) {
        const result: DataSet = {};

        // Process each mapped worksheet
        for (const table of importMappings) {
            if (!!table.Source && !!table.Target) {
                const targetDataset: DataTable = [];

                // Transform each source record to target format
                for (const sourceRecord of importData?.[table.Source]) {
                    const targetRecord: DataTableRow = {};

                    // Map fields according to column mappings
                    for (const mapping of table.Target.Columns) {
                        if (!!mapping.Target)
                            targetRecord[mapping.Target ?? ""] =
                                sourceRecord[mapping.Name ?? ""];
                    }

                    targetDataset.push(targetRecord);
                }

                // Add compiled data package to result
                result[table.Target.Name] = targetDataset;
            }
        }

        // Send compiled data to parent via callback
        this.props.onReady?.(importMappings, result);
    }

    render(): React.ReactNode {
        let classes = "import";

        if (this.props.className) classes += ` ${this.props.className}`;

        return (
            <Panel className={classes}>
                <div className="flex-row import-buttons">
                    <Button
                        icon="file_download"
                        text="Download Template File"
                        onClick={this.downloadTemplate.bind(this)}
                    />
                    <Button
                        theme={ButtonThemes.Theme}
                        text="Select Upload File"
                        onClick={this.uploadTemplate.bind(this)}
                    />
                </div>

                {!this.props.noConfig && this.state.importData && (
                    <>
                        <GeneralContainer
                            fullWidth={true}
                            flex={FlexDirection.Row}
                            flexSpaced={true}
                            clearanceSide={Side.Top}>
                            <Checkbox
                                value={this.state.firstRowHeader}
                                onChange={value =>
                                    this.setState({ firstRowHeader: value })
                                }
                                label="First rows contain field names"
                                tooltip="Does the first row of your import data contain field names?"
                            />
                        </GeneralContainer>

                        <GeneralContainer
                            fullWidth={true}
                            flex={FlexDirection.Row}
                            flexSpaced={true}
                            clearanceSide={Side.Top}>
                            <GeneralContainer
                                flex={FlexDirection.Column}
                                flexBehaviour={FlexBehaviour.Max}>
                                Source Sheet (Worksheet from XLSX or CSV file):
                                <DropDown
                                    noType={true}
                                    fullWidth={true}
                                    options={this.state.sourceTableItems}
                                    value={this.state.selectedSource}
                                    onChange={this.sourceChanged.bind(this)}
                                />
                            </GeneralContainer>
                            <GeneralContainer
                                flex={FlexDirection.Column}
                                flexBehaviour={FlexBehaviour.Max}>
                                Target Type (Datatype to import source to):
                                <DropDown
                                    noType={true}
                                    fullWidth={true}
                                    options={this.state.targetTableItems}
                                    value={this.state.selectedTarget}
                                    onChange={this.targetChanged.bind(this)}
                                />
                            </GeneralContainer>
                        </GeneralContainer>
                    </>
                )}

                {!this.props.noConfig &&
                    this.state.selectedMapping?.Target?.Columns?.length > 0 && (
                        <ImportGrid
                            mappings={this.state.selectedMapping}
                            data={this.state.displayData}
                            columnChanged={this.columnChanged.bind(this)}
                        />
                    )}
            </Panel>
        );
    }
}

export default Import;
